import { Loader2, StickyNote } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { api } from '@/services/api'
import type { CreateInvestigationNoteResponse } from '@/services/api/graph'

interface CreateNoteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  caseId: string
  nodeId?: string | null
  /** Receives the full backend response so the active investigation can
   *  merge the new note + HAS_NOTE edge into its local graphView. Without
   *  this the PostgreSQL graphView snapshot stays unaware of the freshly-
   *  created Neo4j node and the canvas renders nothing. */
  onSuccess: (result: CreateInvestigationNoteResponse) => void
}

export function CreateNoteDialog({
  open,
  onOpenChange,
  caseId,
  nodeId,
  onSuccess,
}: CreateNoteDialogProps) {
  const [noteText, setNoteText] = useState('')
  const [isSticky, setIsSticky] = useState(true)
  const [isCreating, setIsCreating] = useState(false)

  const resetForm = () => {
    setNoteText('')
    setIsSticky(true)
  }

  const handleClose = (open: boolean) => {
    onOpenChange(open)
    if (!open) {
      resetForm()
    }
  }

  const handleCreate = async () => {
    if (!noteText.trim() || isCreating) return

    setIsCreating(true)
    try {
      const result = await api.graph.createNote(caseId, {
        text: noteText.trim(),
        isSticky: isSticky,
        x: undefined,
        y: undefined,
        connected_node_ids: nodeId ? [nodeId] : undefined,
      })

      toast.success('Note created successfully')
      onSuccess(result)
      handleClose(false)
    } catch (err) {
      console.error('Failed to create note:', err)
      toast.error('Failed to create note')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Investigation Note</DialogTitle>
          <DialogDescription>
            Create a note that will be saved to the knowledge graph.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="note-text">Note Text</Label>
            <Textarea
              id="note-text"
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Enter your investigation note..."
              className="min-h-[100px]"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="note-sticky"
              checked={isSticky}
              onCheckedChange={checked => setIsSticky(checked as boolean)}
            />
            <Label htmlFor="note-sticky" className="text-sm font-medium leading-none">
              Show as sticky note in graph
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!noteText.trim() || isCreating}
            className="bg-yellow-600 hover:bg-yellow-700 text-white"
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <StickyNote className="mr-2 h-4 w-4" />
                Create Note
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
